import {
  Avatar,
  Card,
  CardHeader,
  Divider,
  List,
  ListSubheader,
  Skeleton,
} from '@mui/material';
import { useGetHomeTagsQuery, useGetNearByItemsQuery } from 'api/public/b2b';
import { LatLng } from 'api/public/place/types';
import { B2BItem } from 'api/public/types';
import { messages } from 'features/main/messages';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import EmptyState from 'shared/components/EmptyState/EmptyState';
import SkeletonCard from 'shared/components/ListProgress/components/SkeletonCard';
import PageHeader from 'shared/components/PageHeader/PageHeader';
import PageNotFound from 'shared/components/PageNotFound/PageNotFound';
import { PAGE_INDEX, PAGE_SIZE, SEARCH_PARAM } from 'shared/constants/common';
import useScrollPosition from 'shared/hooks/useScrollPosition';
import { useTypedDispatch } from 'shared/redux/store';
import { uniqueArray } from 'shared/utils/common';
import { logEvent } from 'shared/utils/gtm';
import CategoryListItem from '../Categories/components/CategoryListItem/CategoryListItem';
import { useMyLocation } from '../Location/hooks/useMyLocation';
import { placeSlice } from '../Location/slice';

const Tag = () => {
  const scrollPosition = useScrollPosition();
  const { t } = useTranslation();
  const { tagSlug = '' } = useParams();
  const myLocation = useMyLocation();

  const { search } = useLocation();

  const { data: tags, isFetching: isFetchingTags } = useGetHomeTagsQuery();

  const tag = tags?.find((t) => t.slug === tagSlug);

  const [dataPageIndex, setDataPageIndex] = useState<number>(PAGE_INDEX);
  const [dataItems, setDataItems] = useState<B2BItem[]>([]);

  const { data: items, isFetching: isFetchingItems } = useGetNearByItemsQuery(
    {
      ...({ lat: myLocation?.lat, lng: myLocation?.lng } as LatLng),
      tags: tag?._id,
      page: dataPageIndex,
      size: PAGE_SIZE,
    },
    {
      skip: !tag || tag?.tag !== 'B2B_ITEM',
      // refetchOnMountOrArgChange: true,
    }
  );

  const navigate = useNavigate();
  const dispatch = useTypedDispatch();

  const [searchParams] = useSearchParams();
  const lat = searchParams.get(SEARCH_PARAM.LAT) || '';
  const lng = searchParams.get(SEARCH_PARAM.LNG) || '';

  useEffect(() => {
    if (!myLocation) {
      if (lat && lng) {
        dispatch(
          placeSlice.actions.setGPSLatLng({
            lat: +lat,
            lng: +lng,
          })
        );
        dispatch(placeSlice.actions.setGPSDetecting(true));
      } else {
        navigate({ pathname: '/location', search });
      }
    }
  }, [dispatch, lat, lng, myLocation]);

  useEffect(() => {
    if (items) {
      setDataItems((value) => {
        return uniqueArray([...value, ...items]);
      });
    }
  }, [items]);

  if (!tag && !isFetchingTags) {
    return <PageNotFound />;
  }

  return (
    <>
      <Helmet>
        <title>{tag?.title_vi_vn ?? t(messages.totalItems())}</title>
        <meta
          name='description'
          content={tag?.title_vi_vn ?? t(messages.totalItems())}
        />
      </Helmet>
      <PageHeader
        title={
          !tag?.title_vi_vn || scrollPosition <= 52 ? (
            t(messages.totalItems())
          ) : (
            <span className='flex flex-row flex-nowrap items-center justify-center space-x-2 truncate'>
              {tag?.icon_image_url && (
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: 'divider',
                    backgroundColor: 'grey.200',
                  }}
                  alt={tag.title_vi_vn}
                  src={tag.icon_image_url}
                />
              )}{' '}
              <span className='inline-block truncate'>{tag?.title_vi_vn}</span>
            </span>
          )
        }
        backLink={'/'}
      />
      <InfiniteScroll
        scrollThreshold={0.75}
        scrollableTarget={isMobile ? 'body' : 'scrollableTarget'}
        dataLength={(dataItems && dataItems.length) ?? 0} //This is important field to render the next data
        next={() => setDataPageIndex((o) => o + 1)}
        hasMore={!!(items?.length === PAGE_SIZE)}
        loader={
          <List>
            {Array.from(Array(3).keys()).map((item) => (
              <li key={item}>
                <Divider />
                <SkeletonCard />
              </li>
            ))}
          </List>
        }
      >
        <List className='divide-y divide-[#f3f4f8]'>
          <ListSubheader disableGutters>
            <Card elevation={0} sx={{ borderRadius: 0, width: '100%' }}>
              {isFetchingTags ? (
                <CardHeader
                  avatar={
                    <Skeleton
                      variant='circular'
                      animation='wave'
                      width={44}
                      height={44}
                    />
                  }
                  title={<Skeleton animation='wave' width='50%' height={28} />}
                  subheader={
                    <Skeleton animation='wave' width='35%' height={20} />
                  }
                />
              ) : (
                <CardHeader
                  avatar={
                    tag?.icon_image_url && (
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          borderWidth: 1,
                          borderStyle: 'solid',
                          borderColor: 'divider',
                          backgroundColor: 'grey.200',
                        }}
                        alt={tag.title_vi_vn}
                        src={tag.icon_image_url}
                      />
                    )
                  }
                  title={tag?.title_vi_vn}
                  titleTypographyProps={{
                    variant: 'subtitle1',
                    color: 'grey.100',
                  }}
                  subheader={tag?.description_vi_vn}
                  subheaderTypographyProps={{
                    variant: 'body2',
                    color: 'grey.600',
                  }}
                />
              )}
            </Card>
          </ListSubheader>

          <Divider component='li' sx={{ borderTopWidth: '12px !important' }} />
          {dataItems && dataItems.length > 0 ? (
            dataItems.map((item) => (
              <CategoryListItem
                key={item._id}
                item={item}
                onClick={() =>
                  logEvent('Category_detail_click_Item_detail', {
                    category: tag?.title_vi_vn,
                    item_name: item.title_vi_vn,
                  })
                }
              />
            ))
          ) : isFetchingItems || isFetchingTags ? (
            <>
              {Array.from(Array(3).keys()).map((item) => (
                <li key={item}>
                  <SkeletonCard />
                </li>
              ))}
            </>
          ) : (
            <EmptyState title='Trong này chưa có mặt hàng nào. Vui lòng quay lại sau bạn nhé!' />
          )}
        </List>
      </InfiniteScroll>
    </>
  );
};

export default Tag;
